/**
 * Per-source dashboard: filterable pivoted rate matrix (parts x sizes) plus
 * an analytics panel (summary, by-material/description, hours-vs-size curve).
 * Used for OTTAV_61075, OTTAV_LATEST (adds the version-diff tab) and MCAA.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer, Legend,
} from 'recharts';
import {
  styles, ACCENT, CHART_COLORS, StatCard, Spinner, ErrorBox,
  fmtHours, fmtInt, apiGet, errMsg,
} from './rbShared';
import OttavDiff from './OttavDiff';

const FILTER_COLS = [
  { key: 'sheet_name', label: 'Sheet' },
  { key: 'category', label: 'Category' },
  { key: 'description_norm', label: 'Description' },
  { key: 'material_norm', label: 'Material' },
  { key: 'sch_norm', label: 'Sch' },
  { key: 'rating', label: 'Rating' },
  { key: 'end_type', label: 'End' },
];

export default function SourceDashboard({ source, title, showOttavDiff = false }) {
  const [view, setView] = useState('table');
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, alignItems: 'center' }}>
        <span style={{ fontWeight: 700, color: '#111827' }}>{title}</span>
        <div style={{ marginLeft: 12, display: 'flex', gap: 4 }}>
          {['table', 'analytics', ...(showOttavDiff ? ['diff'] : [])].map((v) => (
            <button
              key={v}
              style={{ ...(view === v ? styles.btn : styles.btnGhost), padding: '4px 12px' }}
              onClick={() => setView(v)}
            >
              {v === 'table' ? 'Rate Table' : v === 'analytics' ? 'Analytics' : 'Diff vs 61075'}
            </button>
          ))}
        </div>
      </div>
      {view === 'table' && <RateMatrix source={source} />}
      {view === 'analytics' && <SourceAnalytics source={source} />}
      {view === 'diff' && <OttavDiff />}
    </div>
  );
}

function RateMatrix({ source }) {
  const { instance, accounts } = useMsal();
  const [data, setData] = useState(null);
  const [distinct, setDistinct] = useState({});
  const [filters, setFilters] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    apiGet(instance, accounts, `/rate-benchmarks/distinct?source=${source}`)
      .then((r) => setDistinct(r.data || {}))
      .catch(() => {});
  }, [instance, accounts, source]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ source, pivot: '1', page: String(page), pageSize: '50' });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      if (search) params.set('search', search);
      const r = await apiGet(instance, accounts, `/rate-benchmarks/entries?${params}`);
      setData(r);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, source, filters, search, page]);

  useEffect(() => { load(); }, [load]);

  // Only show size columns that have at least one value on the current page.
  const visibleCols = useMemo(() => {
    if (!data) return [];
    const used = new Set();
    (data.data || []).forEach((row) => Object.keys(row.cells || {}).forEach((k) => used.add(k)));
    return (data.sizeColumns || []).filter((c) => used.has(c.size_raw));
  }, [data]);

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
        <input
          style={{ ...styles.input, width: 200 }}
          placeholder="Search description / key…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        {FILTER_COLS.map(({ key, label }) => (
          (distinct[key] && distinct[key].length > 0) ? (
            <select key={key} style={styles.select} value={filters[key] || ''} onChange={(e) => setFilter(key, e.target.value)}>
              <option value="">{label}: all</option>
              {distinct[key].map((o) => (
                <option key={o.v} value={o.v}>{o.v} ({o.n})</option>
              ))}
            </select>
          ) : null
        ))}
        {(Object.values(filters).some(Boolean) || search) && (
          <button style={styles.btnGhost} onClick={() => { setFilters({}); setSearchInput(''); }}>Clear</button>
        )}
      </div>

      {error && <ErrorBox error={error} onRetry={load} />}
      {loading && !data && <Spinner label="Loading rate table…" />}

      {data && (
        <>
          <div style={{ marginBottom: 6, fontSize: '0.78rem', color: '#6b7280' }}>
            {fmtInt(data.total)} parts · page {data.page}/{data.pageCount || 1}
            {loading ? ' · refreshing…' : ''}
          </div>
          <div style={styles.scroll}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, position: 'sticky', left: 0, zIndex: 3 }}>Description</th>
                  <th style={styles.th}>Material</th>
                  <th style={styles.th}>Sch</th>
                  <th style={styles.th}>Rating</th>
                  <th style={styles.th}>End</th>
                  <th style={styles.th}>Sheet · Row</th>
                  {visibleCols.map((c) => (
                    <th key={c.size_raw} style={{ ...styles.th, textAlign: 'right' }}>{c.size_raw}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data.data || []).map((row) => (
                  <tr key={row.id}>
                    <td style={{ ...styles.td, position: 'sticky', left: 0, background: '#fff', fontWeight: 600, zIndex: 1 }} title={row.description_raw}>
                      {row.description_norm || row.description_raw}
                    </td>
                    <td style={styles.td}>{row.material_norm || ''}</td>
                    <td style={styles.td}>{row.sch_norm || ''}</td>
                    <td style={styles.td}>{row.rating || ''}</td>
                    <td style={styles.td}>{row.end_type || ''}</td>
                    <td style={{ ...styles.td, color: '#9ca3af', fontSize: '0.72rem' }}>{row.sheet_name} · {row.row_ref}</td>
                    {visibleCols.map((c) => (
                      <td key={c.size_raw} style={styles.tdNum}>
                        {row.cells && row.cells[c.size_raw] !== undefined ? fmtHours(row.cells[c.size_raw]) : ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
            <button style={styles.btnGhost} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>← Prev</button>
            <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>Page {data.page} of {data.pageCount || 1}</span>
            <button style={styles.btnGhost} disabled={page >= (data.pageCount || 1)} onClick={() => setPage((p) => p + 1)}>Next →</button>
          </div>
        </>
      )}
    </div>
  );
}

function SourceAnalytics({ source }) {
  const { instance, accounts } = useMsal();
  const [a, setA] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setError(null);
    apiGet(instance, accounts, `/rate-benchmarks/analytics?source=${source}`)
      .then(setA)
      .catch((e) => setError(errMsg(e)));
  }, [instance, accounts, source]);

  useEffect(() => { load(); }, [load]);

  if (error) return <ErrorBox error={error} onRetry={load} />;
  if (!a) return <Spinner label="Computing analytics…" />;

  const s = a.summary || {};
  return (
    <div>
      <div style={styles.statGrid}>
        <StatCard label="Parts" value={fmtInt(s.parts)} sub={`${fmtInt(s.empty_parts)} without values`} />
        <StatCard label="Rate cells" value={fmtInt(s.entries)} />
        <StatCard label="Materials" value={fmtInt(s.materials)} />
        <StatCard label="Descriptions" value={fmtInt(s.descriptions)} />
        <StatCard label="Sizes" value={fmtInt(s.sizes)} />
        <StatCard label="Avg hours" value={fmtHours(s.avg_hours)} sub={`min ${fmtHours(s.min_hours)} · max ${fmtHours(s.max_hours)}`} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 12 }}>
        <div style={styles.card}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Hours vs size (straight sizes, avg + median)</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={a.sizeCurve || []} margin={{ left: 0, right: 12 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="size_in" tick={{ fontSize: 10 }} label={{ value: 'size (in)', position: 'insideBottomRight', offset: -4, fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => fmtHours(v)} labelFormatter={(v) => `${v}"`} />
              <Legend wrapperStyle={{ fontSize: '0.72rem' }} />
              <Line type="monotone" dataKey="avg_hours" name="avg" stroke={ACCENT} dot={false} strokeWidth={2} />
              <Line type="monotone" dataKey="median_hours" name="median" stroke="#b45309" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.card}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Rate cells by material (top 15)</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={(a.byMaterial || []).slice(0, 15)} margin={{ left: 0, right: 12 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={0} angle={-35} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="entries" name="cells" fill={CHART_COLORS[1]} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.card}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Rate cells by description (top 15)</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={(a.byDescription || []).slice(0, 15)} margin={{ left: 0, right: 12 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 9 }} interval={0} angle={-35} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="entries" name="cells" fill={CHART_COLORS[2]} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.card}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Sheets</div>
          <div style={{ maxHeight: 260, overflow: 'auto' }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Sheet</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Parts</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Cells</th>
                </tr>
              </thead>
              <tbody>
                {(a.bySheet || []).map((r) => (
                  <tr key={r.label}>
                    <td style={styles.td}>{r.label}</td>
                    <td style={styles.tdNum}>{fmtInt(r.parts)}</td>
                    <td style={styles.tdNum}>{fmtInt(r.entries)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
